import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, EditPage, FuseInput, LoanSelect } from '/src/components';
import { useAdvanceStore, useFilterStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { DataModelType, FormErrors, UpdateAdvanceForm } from '/src/lib/types';
import { currency, isNumber } from '/src/utils';

/**
 * Edit Advance Page
 */
export const EditAdvancePage: React.FC = observer(() => {
  /** Context **/
  const advanceStore = useAdvanceStore();
  const filterStore = useFilterStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<UpdateAdvanceForm>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Files
  ]);

  /** Computed **/
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formValues?.loan) errors.loan = 'Loan is required';
    if (formValues?.loan.isOwned && !formValues?.asset) errors.asset = 'Asset is required';
    if (!formValues?.name) errors.name = 'Name is required';
    if (!isNumber(formValues?.amount)) errors.amount = 'Valid amount is required';
    if (!formValues?.dateFulfilled) errors.dateFulfilled = 'Date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Advance.
   *
   * @remarks
   * Redirects to the Advance's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await advanceStore.update(formValues);

      if (!response) throw new Error(`Could not update Advance with ID "${formValues?._id}"`);

      toast.success(toastMessages.updateAdvance.success);
      navigate(`/advances/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateAdvance.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!formValues) return;

    await advanceStore.update({ ...formValues, notes });

    updateFormValue('notes')(notes);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!formValues) return;

    await advanceStore.update({ ...formValues, files });

    updateFormValue('files')(files);
  };

  /** Effects **/

  useEffect(() => {
    if (params.advanceId) advanceStore.get(params.advanceId);
  }, [params.advanceId]);

  useEffect(() => {
    if (advanceStore.currentAdvance) setFormValues(advanceStore.currentAdvance);
  }, [advanceStore.currentAdvance]);

  /** Render **/
  return (
    <EditPage
      modelType={DataModelType.Advance}
      editVerb="Edit"
      onEdit={update}
      sidebarFlex="35"
      contentFlex="65"
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      noteOptions={{
        notes: formValues?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        files: formValues?.files ?? [],
        onChange: updateFiles,
      }}
    >
      <LoanSelect
        value={formValues?.loan?._id ?? ''}
        setValue={(loanId) => updateFormValue('loan')(loans.find((loan) => loan._id === loanId))}
        loans={loans}
        error={formErrors.loan}
        disabled
      />

      {formValues?.asset && (
        <AssetSelect
          value={formValues?.asset?._id ?? ''}
          setValue={() => {}}
          assets={[formValues?.asset]}
          error={formErrors.asset}
          disabled
        />
      )}

      <Box display="flex">
        <Box display="flex" flexDirection="column" flex={1}>
          <FuseInput
            id="name"
            label="Advance Name"
            value={formValues?.name}
            updateValue={updateFormValue('name')}
            required
            error={formErrors.name}
          />

          <FuseInput
            id="amount"
            label="Amount"
            value={formValues?.amount}
            updateValue={updateFormValue('amount')}
            type="number"
            moneyMask
            negativeNumber
            required
            error={formErrors.amount}
          />
        </Box>

        <Box display="flex" flexDirection="column" flex={1}>
          {formValues?.loan && !formValues?.loan.isOwned && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              className="md-title"
              height="100%"
            >
              <Box paddingBottom="6px" marginBottom="6px" borderBottom="solid 1px rgba(0,0,0,0.12)" className="thin">
                Available Credit
              </Box>
              <Box id="currentValue">
                {formValues?.loan.assetValue
                  ? currency.format(
                      formValues?.loan.assetValue * formValues?.loan.advanceRate - formValues?.loan.principalBalance
                    )
                  : '-'}
              </Box>
            </Box>
          )}

          {formValues?.loan && formValues?.loan.isOwned && !formValues?.asset && (
            <Box display="flex" justifyContent="center" alignItems="center" className="md-title thin" height="100%">
              Please Select an Asset.
            </Box>
          )}

          {formValues?.loan?.isOwned && formValues?.asset && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              className="md-title thin"
              height="100%"
              flexDirection="column"
              gap="16px"
            >
              <Box display="flex" width="100%">
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Asset Value
                  </Box>
                  {formValues?.asset?.originalValue && (
                    <Box id="originalValue">{currency.format(formValues?.asset?.originalValue)}</Box>
                  )}
                  {!formValues?.asset?.originalValue && (
                    <Box className="md-caption center thin">
                      <strong>No Asset value found.</strong>
                    </Box>
                  )}
                </Box>
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Initial Payment Amount
                  </Box>
                  {formValues?.asset?.initialPaymentAmount && (
                    <Box id="initialPaymentAmount">{currency.format(formValues?.asset?.initialPaymentAmount)}</Box>
                  )}
                  {!formValues?.asset?.initialPaymentAmount && (
                    <Box className="md-caption center thin">
                      <strong>No initial payment amount found.</strong>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box display="flex" width="100%">
                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Socket Fees
                  </Box>
                  <Box id="socketFees">
                    {currency.format(
                      (formValues?.asset.adminFee || 0) +
                        (formValues?.asset.discountFee || 0) +
                        (formValues?.asset.lateFee || 0)
                    )}
                  </Box>
                </Box>

                <Box
                  flex={50}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  className="md-title"
                  height="100%"
                >
                  <Box
                    paddingBottom="6px"
                    marginBottom="6px"
                    borderBottom="solid 1px rgba(0,0,0,0.12)"
                    className="thin"
                  >
                    Expected Residual Payment
                  </Box>
                  {typeof formValues?.asset.expectedResidualPayment === 'number' && (
                    <Box id="expectedResidualPayment">{currency.format(formValues?.asset.expectedResidualPayment)}</Box>
                  )}
                  {/* {!assetLoaded && (
                    <Box className="md-caption center thin">
                      <strong>Calculating...</strong>
                    </Box>
                  )} */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" gap="16px">
        <FuseInput
          label="Date Requested"
          value={formValues?.dateRequested}
          updateValue={updateFormValue('dateRequested')}
          type="date"
          required
          error={formErrors.dateRequested}
        />
        <FuseInput
          label="Date Fulfilled"
          value={formValues?.dateFulfilled}
          updateValue={updateFormValue('dateFulfilled')}
          type="date"
          required
          error={formErrors.dateFulfilled}
        />
      </Box>
    </EditPage>
  );
});
